// Web "Me" page — full personalization hub, ported from the mobile ProfileScreen
// and its sheets (EditProfile, Interests, Memberships, Location). Wired to the
// shared Django profile endpoints via profileService. Token-styled (light/dark).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Pencil, X, Sparkles, LogOut, Wallet as WalletIcon,
  Moon, Sun, Repeat, Users, CheckCircle2, CalendarDays, Crosshair,
} from 'lucide-react';
import {
  getProfile, updateProfile, getPreferences, savePreferences,
  getSignals, dismissSignal, getMemberships, saveMemberships, getWeeklySummary,
  CATEGORY_CATALOG, MEMBERSHIP_CATALOG,
} from '../services/profileService';
import { fetchRoutines } from '../services/routineService';
import { fetchUserGroups } from '../services/groupService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './ProfilePage.css';

// Downscale + compress an image File to a small JPEG data URI for avatar_image.
function fileToAvatar(file, max = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const memberSince = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } catch (_) { return '—'; }
};

function ProfilePage() {
  const navigate = useNavigate();
  const { refresh, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const [profile, setProfile] = useState(null);
  const [prefs, setPrefs] = useState({});       // { category: {enabled, instructions, priority} }
  const [memberships, setMemberships] = useState({}); // { store: {tier, note} }
  const [signals, setSignals] = useState({ suggestions: [], activity: {} });
  const [counts, setCounts] = useState({ routines: 0, groups: 0, completed: 0 });
  const [weekly, setWeekly] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingMems, setSavingMems] = useState(false);
  const [savingLoc, setSavingLoc] = useState(false);
  const [loc, setLoc] = useState({ city: '', region: '', country: '', lat: null, lng: null });

  const load = useCallback(async () => {
    setLoading(true);
    const [p, pr, mem, sig, weeklyTasks, routines, groups] = await Promise.allSettled([
      getProfile(), getPreferences(), getMemberships(), getSignals(),
      getWeeklySummary(), fetchRoutines(), fetchUserGroups(),
    ]);
    if (p.status === 'fulfilled') {
      setProfile(p.value);
      setLoc({
        city: p.value.location_city || '', region: p.value.location_region || '',
        country: p.value.location_country || '', lat: p.value.location_lat, lng: p.value.location_lng,
      });
    }
    if (pr.status === 'fulfilled') {
      const map = {};
      pr.value.forEach((x) => { map[x.category] = { enabled: x.enabled, instructions: x.instructions || '', priority: x.priority || 0 }; });
      setPrefs(map);
    }
    if (mem.status === 'fulfilled') {
      const map = {};
      mem.value.forEach((x) => { map[x.store] = { tier: x.tier || '', note: x.note || '' }; });
      setMemberships(map);
    }
    if (sig.status === 'fulfilled') setSignals({ suggestions: sig.value.suggestions || [], activity: sig.value.activity || {} });
    setCounts({
      completed: weeklyTasks.status === 'fulfilled' ? weeklyTasks.value.length : 0,
      routines: routines.status === 'fulfilled' ? (routines.value || []).length : 0,
      groups: groups.status === 'fulfilled' ? (groups.value || []).length : 0,
    });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── interests ──
  const setPref = (key, patch) => setPrefs((m) => ({ ...m, [key]: { enabled: false, instructions: '', priority: 0, ...m[key], ...patch } }));
  const appendInstruction = (key, chip) => setPrefs((m) => {
    const cur = m[key]?.instructions || '';
    const next = cur ? `${cur}, ${chip}` : chip;
    return { ...m, [key]: { enabled: true, priority: 0, ...m[key], instructions: next } };
  });
  const saveInterests = async () => {
    setSavingPrefs(true);
    try {
      const list = CATEGORY_CATALOG.map((c) => ({
        category: c.key,
        enabled: !!prefs[c.key]?.enabled,
        instructions: prefs[c.key]?.instructions || '',
        priority: prefs[c.key]?.priority || 0,
      }));
      await savePreferences(list);
    } finally { setSavingPrefs(false); }
  };

  const acceptSuggestion = async (s) => {
    const key = s.pref_category;
    if (key) appendInstruction(key, s.value);
    await dismissSignal(s.id);
    setSignals((st) => ({ ...st, suggestions: st.suggestions.filter((x) => x.id !== s.id) }));
  };
  const skipSuggestion = async (s) => {
    await dismissSignal(s.id);
    setSignals((st) => ({ ...st, suggestions: st.suggestions.filter((x) => x.id !== s.id) }));
  };

  // ── memberships ──
  const toggleMembership = (store, def) => setMemberships((m) => {
    const next = { ...m };
    if (next[store]) delete next[store];
    else next[store] = { tier: def.tiers ? def.tiers[0].key : '', note: '' };
    return next;
  });
  const setTier = (store, tier) => setMemberships((m) => ({ ...m, [store]: { ...(m[store] || {}), tier } }));
  const saveMembershipsList = async () => {
    setSavingMems(true);
    try {
      const list = Object.entries(memberships).map(([store, v]) => ({ store, tier: v.tier || '', note: v.note || '' }));
      await saveMemberships(list);
    } finally { setSavingMems(false); }
  };

  // ── location ──
  const useCurrentLocation = () => {
    if (!navigator.geolocation) { window.alert('Geolocation not supported.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setLoc((l) => ({ ...l, lat: pos.coords.latitude, lng: pos.coords.longitude })),
      () => window.alert('Could not get your location.'),
    );
  };
  const saveLocation = async () => {
    setSavingLoc(true);
    try {
      const updated = await updateProfile({
        location_city: loc.city, location_region: loc.region, location_country: loc.country,
        location_lat: loc.lat, location_lng: loc.lng, location_source: 'manual',
      });
      setProfile(updated);
    } finally { setSavingLoc(false); }
  };

  // ── weekly ──
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const loadWeekly = async () => {
    setLoadingWeekly(true);
    try { setWeekly(await getWeeklySummary()); }
    finally { setLoadingWeekly(false); }
  };

  const doLogout = () => {
    if (!window.confirm('Sign out?')) return;
    logout();
    navigate('/login');
  };

  const initial = (profile?.display_name || profile?.username || 'U').charAt(0).toUpperCase();

  if (loading) return <div className="me-screen"><div className="me-loading">Loading your profile…</div></div>;

  return (
    <div className="me-screen">
      <div className="me-inner">
        {/* Header */}
        <section className="me-header">
          <div className="me-avatar-wrap">
            {profile?.avatar_image
              ? <img className="me-avatar-img" src={profile.avatar_image} alt="avatar" />
              : <div className="me-avatar">{initial}</div>}
            <button className="me-avatar-edit" onClick={() => setEditing(true)} aria-label="Edit profile"><Camera size={15} /></button>
          </div>
          <h1 className="me-name">{profile?.display_name || profile?.username}</h1>
          <p className="me-handle">@{profile?.username}</p>
          {profile?.bio && <p className="me-bio">{profile.bio}</p>}
          <span className="me-since"><CalendarDays size={13} /> Member since {memberSince(profile?.date_joined)}</span>
          <button className="me-edit-btn" onClick={() => setEditing(true)}><Pencil size={14} /> Edit profile</button>
        </section>

        {/* Stats */}
        <section className="me-stats">
          <Stat icon={<CheckCircle2 size={18} />} value={counts.completed} label="Done this week" />
          <Stat icon={<Repeat size={18} />} value={counts.routines} label="Routines" />
          <Stat icon={<Users size={18} />} value={counts.groups} label="Groups" />
        </section>

        {/* Quick links */}
        <button className="me-link-card" onClick={() => navigate('/wallet')}>
          <WalletIcon size={18} /> <span>My Wallet</span> <span className="me-link-chevron">›</span>
        </button>

        {/* Account info */}
        <Card title="Account">
          <Row label="Email" value={profile?.email || '—'} />
          <Row label="Username" value={profile?.username} />
          <Row label="Account type" value={profile?.role === 'admin' ? 'Admin' : profile?.role === 'merchant' ? 'Merchant' : 'Free Plan'} />
          <Row label="Status" value={<span className="me-badge-active">Active</span>} />
        </Card>

        {/* Interests */}
        <Card
          title="Interests"
          subtitle="Tune the AI recommendations per category"
          action={<button className="me-save-btn" disabled={savingPrefs} onClick={saveInterests}>{savingPrefs ? 'Saving…' : 'Save'}</button>}
        >
          {signals.suggestions.length > 0 && (
            <div className="me-suggest-rail">
              {signals.suggestions.map((s) => (
                <div key={s.id} className="me-suggest-card">
                  <span className="me-suggest-eyebrow"><Sparkles size={12} /> Suggested</span>
                  <span className="me-suggest-label">{s.label}</span>
                  <span className="me-suggest-sub">{s.subtitle}</span>
                  <div className="me-suggest-actions">
                    <button className="me-suggest-accept" onClick={() => acceptSuggestion(s)}>Add</button>
                    <button className="me-suggest-skip" onClick={() => skipSuggestion(s)}>Skip</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="me-interest-list">
            {CATEGORY_CATALOG.map((c) => {
              const on = !!prefs[c.key]?.enabled;
              const activity = signals.activity?.[c.key];
              return (
                <div key={c.key} className={`me-interest${on ? ' on' : ''}`}>
                  <div className="me-interest-head">
                    <span className="me-interest-title">{c.icon} {c.label}</span>
                    {activity ? <span className="me-interest-activity">{activity} this month</span> : null}
                    <span className={`me-switch${on ? ' on' : ''}`} onClick={() => setPref(c.key, { enabled: !on })}><span /></span>
                  </div>
                  {on && (
                    <>
                      <input
                        className="me-interest-input"
                        placeholder="Add instructions (e.g. organic only)…"
                        value={prefs[c.key]?.instructions || ''}
                        onChange={(e) => setPref(c.key, { instructions: e.target.value })}
                      />
                      <div className="me-chip-row">
                        {c.examples.map((ex) => (
                          <button key={ex} className="me-ex-chip" onClick={() => appendInstruction(c.key, ex)}>+ {ex}</button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Memberships */}
        <Card
          title="Memberships"
          subtitle="Used to surface deals you can actually use"
          action={<button className="me-save-btn" disabled={savingMems} onClick={saveMembershipsList}>{savingMems ? 'Saving…' : 'Save'}</button>}
        >
          <div className="me-mem-grid">
            {MEMBERSHIP_CATALOG.map((def) => {
              const on = !!memberships[def.key];
              return (
                <div key={def.key} className={`me-mem${on ? ' on' : ''}`}>
                  <div className="me-mem-head" onClick={() => toggleMembership(def.key, def)}>
                    <span className="me-mem-label">{def.icon} {def.label}</span>
                    <span className={`me-switch${on ? ' on' : ''}`}><span /></span>
                  </div>
                  {on && def.tiers && (
                    <div className="me-chip-row">
                      {def.tiers.map((t) => (
                        <button
                          key={t.key}
                          className={`me-tier-chip${memberships[def.key]?.tier === t.key ? ' active' : ''}`}
                          onClick={() => setTier(def.key, t.key)}
                        >{t.label}</button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Location */}
        <Card
          title="Location"
          subtitle="Scopes deals & nearby recommendations"
          action={<button className="me-save-btn" disabled={savingLoc} onClick={saveLocation}>{savingLoc ? 'Saving…' : 'Save'}</button>}
        >
          <div className="me-loc-grid">
            <input className="me-input" placeholder="City" value={loc.city} onChange={(e) => setLoc((l) => ({ ...l, city: e.target.value }))} />
            <input className="me-input" placeholder="Region / State" value={loc.region} onChange={(e) => setLoc((l) => ({ ...l, region: e.target.value }))} />
            <input className="me-input" placeholder="Country" value={loc.country} onChange={(e) => setLoc((l) => ({ ...l, country: e.target.value }))} />
          </div>
          <button className="me-loc-gps" onClick={useCurrentLocation}>
            <Crosshair size={14} /> Use my current location{loc.lat ? ` (set)` : ''}
          </button>
        </Card>

        {/* Weekly review */}
        <Card title="Weekly review" subtitle="What you completed in the last 7 days">
          {weekly === null ? (
            <button className="me-secondary-btn" disabled={loadingWeekly} onClick={loadWeekly}>
              {loadingWeekly ? 'Loading…' : 'Generate weekly review'}
            </button>
          ) : weekly.length === 0 ? (
            <p className="me-muted">No completed tasks in the last 7 days.</p>
          ) : (
            <>
              <p className="me-weekly-summary">You completed <strong>{weekly.length}</strong> task{weekly.length === 1 ? '' : 's'} this week. Nice work!</p>
              <ul className="me-weekly-list">
                {weekly.map((t) => (
                  <li key={t.id}><span className="me-weekly-cat">{t.category}</span> {t.task_text}</li>
                ))}
              </ul>
            </>
          )}
        </Card>

        {/* Settings */}
        <Card title="Settings">
          <div className="me-row" onClick={toggleTheme} style={{ cursor: 'pointer' }}>
            <span className="me-row-label">{isDark ? <Moon size={15} /> : <Sun size={15} />} Theme</span>
            <span className="me-row-value">{isDark ? 'Dark' : 'Light'}</span>
          </div>
        </Card>

        <div className="me-footer">
          <button className="me-logout" onClick={doLogout}><LogOut size={15} /> Sign out</button>
        </div>
      </div>

      {editing && (
        <EditProfileModal
          profile={profile}
          onClose={() => setEditing(false)}
          onSaved={async (updated) => {
            setProfile(updated);
            setEditing(false);
            try { await refresh(); } catch (_) { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="me-stat">
      <span className="me-stat-icon">{icon}</span>
      <span className="me-stat-value">{value}</span>
      <span className="me-stat-label">{label}</span>
    </div>
  );
}

function Card({ title, subtitle, action, children }) {
  return (
    <section className="me-card">
      <div className="me-card-head">
        <div>
          <h2 className="me-card-title">{title}</h2>
          {subtitle && <p className="me-card-sub">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="me-row">
      <span className="me-row-label">{label}</span>
      <span className="me-row-value">{value}</span>
    </div>
  );
}

function EditProfileModal({ profile, onClose, onSaved }) {
  const [form, setForm] = useState({
    display_name: profile?.display_name || '',
    bio: profile?.bio || '',
    first_name: profile?.first_name || '',
    last_name: profile?.last_name || '',
    avatar_image: profile?.avatar_image || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    try { set({ avatar_image: await fileToAvatar(file) }); }
    catch (_) { window.alert('Could not read that image.'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateProfile({
        display_name: form.display_name.trim(),
        bio: form.bio.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        avatar_image: form.avatar_image,
      });
      onSaved(updated);
    } catch (_) {
      window.alert('Could not save your profile.');
      setSaving(false);
    }
  };

  const initial = (form.display_name || profile?.username || 'U').charAt(0).toUpperCase();

  return (
    <div className="me-modal-overlay" onClick={onClose}>
      <div className="me-modal" onClick={(e) => e.stopPropagation()}>
        <div className="me-modal-head">
          <h2>Edit profile</h2>
          <button className="me-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="me-modal-body">
          <label className="me-avatar-pick">
            {form.avatar_image
              ? <img className="me-avatar-img lg" src={form.avatar_image} alt="avatar" />
              : <div className="me-avatar lg">{initial}</div>}
            <span className="me-avatar-pick-badge"><Camera size={16} /></span>
            <input type="file" accept="image/*" hidden onChange={onPick} />
          </label>

          <label className="me-field"><span>Display name</span>
            <input className="me-input" maxLength={80} value={form.display_name} onChange={(e) => set({ display_name: e.target.value })} />
          </label>
          <div className="me-field-row">
            <label className="me-field"><span>First name</span>
              <input className="me-input" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} />
            </label>
            <label className="me-field"><span>Last name</span>
              <input className="me-input" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} />
            </label>
          </div>
          <label className="me-field"><span>Bio <em>{form.bio.length}/280</em></span>
            <textarea className="me-input me-textarea" maxLength={280} rows={3} value={form.bio} onChange={(e) => set({ bio: e.target.value })} />
          </label>
        </div>
        <div className="me-modal-foot">
          <button className="me-ghost-btn" onClick={onClose}>Cancel</button>
          <button className="me-primary-btn" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
